<template>
  <div class="p-4 space-y-4">
    <LeaveAdminSectionNav />

    <el-card shadow="hover" class="rounded-2xl border border-gray-200">
      <div class="text-base font-semibold mb-1">📋 휴가 일괄 할당</div>
      <div class="text-sm text-gray-500 mb-4">
        선택한 휴가유형을 직원들에게 일괄 할당합니다. 생일반차는 윈도우 기반 별도 트랙으로 자동 처리됩니다.
      </div>

      <el-form :model="bulkForm" label-width="100px">
        <el-row :gutter="16">
          <el-col :xs="24" :md="8">
            <el-form-item label="대상 연도" required>
              <el-input-number v-model="bulkForm.year" :min="2020" :max="2100" controls-position="right" style="width:100%;" @change="resetPreview" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="휴가유형" required>
              <el-select v-model="bulkForm.leave_type" placeholder="휴가유형 선택" style="width:100%;" @change="resetPreview">
                <el-option v-for="lt in leaveTypeOptions" :key="lt.id" :label="lt.name" :value="lt.id">
                  <span>{{ lt.name }}</span>
                  <span v-if="lt.name === '생일반차'" style="float:right; color:#e6a23c; font-size:12px;">🎂 윈도우 기반</span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8" v-if="!isSelectedBirthdayLeave">
            <el-form-item label="회사">
              <el-select v-model="bulkForm.company_name" placeholder="전체" clearable style="width:100%;">
                <el-option label="전체" value="" />
                <el-option v-for="company in companyOptions" :key="company" :label="company" :value="company" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 일반 휴가 옵션 -->
        <el-form-item v-if="!isSelectedBirthdayLeave" label="적용 옵션">
          <div style="display:flex; flex-wrap:wrap; gap:16px;">
            <el-checkbox v-model="bulkForm.apply_tenure_rule">근속연수 추가연차 적용</el-checkbox>
            <el-checkbox v-model="bulkForm.apply_carry_forward">전년도 잔여 이월 적용</el-checkbox>
            <el-checkbox v-model="bulkForm.overwrite_existing">기존 할당 덮어쓰기</el-checkbox>
          </div>
        </el-form-item>

        <!-- 생일반차 안내 -->
        <el-alert v-if="isSelectedBirthdayLeave" type="warning" :closable="false" show-icon style="margin-bottom: 12px;">
          <template #title>
            🎂 생일반차는 별도 트랙입니다
          </template>
          <div style="font-size:13px; color:#5a6c7d;">
            활성 직원 중 생일이 등록된 사람에게 정책에 따라 자동 부여됩니다.
            <strong>윈도우 기반</strong> (영업일 ±N일)이며, 이미 부여됐거나 사용 기한이 지난 직원은 자동 스킵됩니다.
          </div>
        </el-alert>
      </el-form>

      <div style="display:flex; gap:8px; margin-bottom:12px;">
        <el-button @click="doPreview" :loading="previewLoading">미리보기</el-button>
        <el-button type="primary" @click="doApply" :loading="applyLoading" :disabled="!canApply">
          일괄 {{ isSelectedBirthdayLeave ? '부여' : '할당' }} 실행
        </el-button>
      </div>

      <!-- 요약 (일반 휴가) -->
      <div v-if="!isSelectedBirthdayLeave && bulkPreviewSummary" class="summary-line">
        <span>대상 <strong>{{ bulkPreviewSummary.count }}</strong>명</span>
        <span>휴가유형: <strong>{{ bulkPreviewSummary.leave_type_name }}</strong></span>
        <span>연도: <strong>{{ bulkPreviewSummary.year }}</strong></span>
      </div>

      <!-- 요약 (생일반차) -->
      <div v-if="isSelectedBirthdayLeave && birthdaySummary" class="summary-line">
        <el-tooltip content="활성 + 생일 등록된 직원 수" placement="top">
          <span>대상 <strong>{{ birthdaySummary.total_candidates }}</strong>명</span>
        </el-tooltip>
        <el-tooltip content="이번에 새로 부여될 직원" placement="top">
          <span>부여 예정 <strong style="color:#67c23a;">{{ birthdaySummary.will_grant }}</strong>명</span>
        </el-tooltip>
        <el-tooltip content="이미 그 해 부여된 직원" placement="top">
          <span>이미 부여 <strong style="color:#909399;">{{ birthdaySummary.skipped_existing }}</strong>명</span>
        </el-tooltip>
        <el-tooltip content="생일이 사용 기한을 지난 직원" placement="top">
          <span>기한 경과 <strong style="color:#f56c6c;">{{ birthdaySummary.skipped_expired }}</strong>명</span>
        </el-tooltip>
        <span style="color:#909399;">윈도우: 생일 -{{ birthdayPolicy?.days_before }}일 ~ +{{ birthdayPolicy?.days_after }}일 (영업일)</span>
      </div>

      <!-- 표: 일반 휴가 -->
      <el-table v-if="!isSelectedBirthdayLeave" :data="bulkPreviewRows || []" size="default" max-height="500" stripe border style="width:100%;">
        <el-table-column prop="employee_name" label="직원명" min-width="100" fixed />
        <el-table-column prop="company_name" label="회사" min-width="120" />
        <el-table-column prop="department_name" label="부서" min-width="120" />
        <el-table-column prop="base_days" label="기본" width="80" align="right" />
        <el-table-column prop="extra_days" label="추가" width="80" align="right" />
        <el-table-column prop="carry_days" label="이월" width="80" align="right" />
        <el-table-column prop="total_days" label="총일수" width="90" align="right">
          <template #default="{ row }"><strong style="color:#409EFF;">{{ row.total_days }}</strong></template>
        </el-table-column>
        <el-table-column label="기존할당" width="100">
          <template #default="{ row }">
            <el-tag :type="row.has_existing_quota ? 'warning' : 'success'" size="small">
              {{ row.has_existing_quota ? '있음' : '없음' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="valid_from" label="사용 시작일" width="120" />
        <el-table-column prop="valid_to" label="사용 종료일" width="120" />
      </el-table>

      <!-- 표: 생일반차 -->
      <el-table v-else-if="birthdayPreviewRows.length" :data="birthdayPreviewRows" size="default" max-height="500" stripe border style="width:100%;">
        <el-table-column prop="employee_name" label="직원명" min-width="100" fixed />
        <el-table-column prop="company_name" label="회사" min-width="120" />
        <el-table-column prop="department_name" label="부서" min-width="120" />
        <el-table-column label="생년월일" width="160">
          <template #default="{ row }">
            <span>{{ row.birth_date }}</span>
            <el-tag v-if="row.is_lunar" type="warning" effect="plain" size="small" style="margin-left:6px;">음력</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="이번해 생일 (양력)" width="160">
          <template #default="{ row }">
            <strong style="color:#e6a23c;">{{ row.birthday_in_year }}</strong>
            <el-tooltip v-if="row.is_lunar" content="음력에서 양력으로 변환된 날짜" placement="top">
              <span style="margin-left:4px; color:#909399; cursor:help;">ⓘ</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="valid_from" label="사용 시작일" width="120" />
        <el-table-column prop="valid_to" label="사용 종료일" width="120" />
        <el-table-column prop="days" label="일수" width="80" align="right" />
        <el-table-column label="상태" width="120" fixed="right">
          <template #header>
            <span>상태</span>
            <el-tooltip content="부여 예정: 새로 부여 / 이미 있음: 그 해 이미 부여됨 / 기한 경과: 사용 기한 지남" placement="top">
              <span style="margin-left:4px; color:#909399; cursor:help;">ⓘ</span>
            </el-tooltip>
          </template>
          <template #default="{ row }">
            <el-tag v-if="row.status === 'WILL_GRANT'" type="success" effect="dark">부여 예정</el-tag>
            <el-tag v-else-if="row.status === 'SKIPPED_EXISTING'" type="info">이미 있음</el-tag>
            <el-tag v-else-if="row.status === 'SKIPPED_EXPIRED'" type="danger">기한 경과</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-else description="미리보기 버튼을 눌러주세요" :image-size="80" />
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import LeaveAdminSectionNav from '@/components/LeaveAdminSectionNav.vue'

const currentYear = new Date().getFullYear()

const bulkPreviewLoading = ref(false)
const bulkApplyLoading = ref(false)
const bulkPreviewSummary = ref(null)
const bulkPreviewRows = ref([])
const leaveTypeOptions = ref([])

// 현재 선택된 휴가유형이 생일반차인지
const isSelectedBirthdayLeave = computed(() => {
  const lt = leaveTypeOptions.value.find((l) => l.id === bulkForm.value.leave_type)
  return lt?.name === '생일반차'
})

// 미리보기 후 실행 가능한지
const canApply = computed(() => {
  if (isSelectedBirthdayLeave.value) return !!birthdaySummary.value && birthdaySummary.value.will_grant > 0
  return bulkPreviewRows.value.length > 0
})

// 폼 변경 시 미리보기 초기화
const resetPreview = () => {
  bulkPreviewRows.value = []
  bulkPreviewSummary.value = null
  birthdayPreviewRows.value = []
  birthdaySummary.value = null
}

// 통합 미리보기 — 휴가유형에 따라 분기
const previewLoading = computed({
  get: () => bulkPreviewLoading.value || birthdayPreviewLoading.value,
  set: () => {}
})
const applyLoading = computed({
  get: () => bulkApplyLoading.value || birthdayApplyLoading.value,
  set: () => {}
})

const doPreview = async () => {
  if (isSelectedBirthdayLeave.value) {
    await previewBirthdayBulk()
  } else {
    await previewBulkAllocation()
  }
}

const doApply = async () => {
  if (isSelectedBirthdayLeave.value) {
    await applyBirthdayBulk()
  } else {
    await applyBulkAllocation()
  }
}
const companyOptions = ref([])

const bulkForm = ref({
  year: currentYear,
  leave_type: null,
  company_name: '',
  apply_tenure_rule: true,
  apply_carry_forward: false,
  overwrite_existing: false,
})

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const extractApiErrorMessage = (err, fallback) => {
  const data = err?.response?.data
  if (!data) return fallback
  if (typeof data === 'string') return data
  if (data.detail) return data.detail
  const firstKey = Object.keys(data)[0]
  if (!firstKey) return fallback
  const value = data[firstKey]
  return Array.isArray(value) ? value[0] : value || fallback
}

const fetchLeaveTypes = async () => {
  try {
    const res = await axiosInstance.get('leave-types/')
    leaveTypeOptions.value = normalizeListResponse(res.data)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '휴가유형 목록 조회에 실패했습니다.'))
    leaveTypeOptions.value = []
  }
}

const fetchCompanyOptions = async (year = currentYear) => {
  try {
    const res = await axiosInstance.get('leave-quota/', { params: { year } })
    const rows = normalizeListResponse(res.data)
    companyOptions.value = [...new Set(rows.map((row) => row.company_name).filter(Boolean))]
  } catch (err) {
    companyOptions.value = []
  }
}

const buildBulkPayload = () => ({
  year: Number(bulkForm.value.year),
  leave_type: bulkForm.value.leave_type,
  company_name: bulkForm.value.company_name || null,
  apply_tenure_rule: !!bulkForm.value.apply_tenure_rule,
  apply_carry_forward: !!bulkForm.value.apply_carry_forward,
  overwrite_existing: !!bulkForm.value.overwrite_existing,
})

const previewBulkAllocation = async () => {
  if (!bulkForm.value.leave_type) {
    ElMessage.warning('휴가유형을 선택해 주세요.')
    return
  }
  if (!bulkForm.value.year) {
    ElMessage.warning('연도를 입력해 주세요.')
    return
  }

  bulkPreviewLoading.value = true
  try {
    const payload = buildBulkPayload()
    const { data } = await axiosInstance.post('leave-quota/bulk-preview/', payload)
    bulkPreviewSummary.value = {
      year: data.year,
      leave_type_name: data.leave_type_name,
      count: data.count || 0,
    }
    bulkPreviewRows.value = Array.isArray(data.rows) ? data.rows : []
    ElMessage.success(`미리보기 ${bulkPreviewRows.value.length}건을 불러왔습니다.`)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '일괄 할당 미리보기에 실패했습니다.'))
  } finally {
    bulkPreviewLoading.value = false
  }
}

const applyBulkAllocation = async () => {
  if (bulkPreviewRows.value.length === 0) {
    ElMessage.warning('먼저 미리보기를 실행해 주세요.')
    return
  }

  try {
    await ElMessageBox.confirm(
      `총 ${bulkPreviewRows.value.length}건을 일괄 할당하시겠습니까?`,
      '일괄 할당 실행',
      {
        type: 'warning',
        confirmButtonText: '실행',
        cancelButtonText: '취소',
      },
    )
  } catch {
    return
  }

  bulkApplyLoading.value = true
  try {
    const payload = buildBulkPayload()
    const { data } = await axiosInstance.post('leave-quota/bulk-apply/', payload)
    const applied = data?.applied ?? 0
    const skipped = data?.skipped_existing ?? 0
    ElMessage.success(`일괄 할당 완료: 적용 ${applied}건, 건너뜀 ${skipped}건`)
    await fetchCompanyOptions(payload.year)
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '일괄 할당 실행에 실패했습니다.'))
  } finally {
    bulkApplyLoading.value = false
  }
}

// 🎂 생일반차 일괄 부여 로직
const birthdayBulkForm = ref({ year: currentYear })
const birthdayPreviewLoading = ref(false)
const birthdayApplyLoading = ref(false)
const birthdayPreviewRows = ref([])
const birthdaySummary = ref(null)
const birthdayPolicy = ref(null)

const previewBirthdayBulk = async () => {
  birthdayPreviewLoading.value = true
  try {
    const { data } = await axiosInstance.get('birthday-grants/bulk-preview/', {
      params: { year: birthdayBulkForm.value.year }
    })
    birthdayPreviewRows.value = data?.rows || []
    birthdaySummary.value = data?.summary || null
    birthdayPolicy.value = data?.policy || null
    if (birthdaySummary.value?.total_candidates === 0) {
      ElMessage.warning('생일이 등록된 활성 직원이 없습니다.')
    } else {
      ElMessage.success('미리보기 ' + birthdaySummary.value.total_candidates + '명 — 부여 예정 ' + birthdaySummary.value.will_grant + '명')
    }
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '생일반차 미리보기 실패'))
  } finally {
    birthdayPreviewLoading.value = false
  }
}

const applyBirthdayBulk = async () => {
  try {
    await ElMessageBox.confirm(
      birthdayBulkForm.value.year + '년 생일반차를 ' + (birthdaySummary.value?.will_grant ?? 0) + '명에게 일괄 부여합니다. 진행하시겠습니까?',
      '생일반차 일괄 부여 확인',
      { confirmButtonText: '실행', cancelButtonText: '취소' }
    )
  } catch { return }

  birthdayApplyLoading.value = true
  try {
    const { data } = await axiosInstance.post('birthday-grants/bulk-grant/', {
      year: birthdayBulkForm.value.year
    })
    ElMessage.success('완료: 부여 ' + data.granted + '건 / 스킵 ' + data.skipped + '건 / 기한경과 ' + (data.skipped_expired || 0) + '건')
    await previewBirthdayBulk()
  } catch (err) {
    ElMessage.error(extractApiErrorMessage(err, '생일반차 일괄 부여 실패'))
  } finally {
    birthdayApplyLoading.value = false
  }
}

onMounted(async () => {
  await fetchLeaveTypes()
  if (!bulkForm.value.leave_type) {
    // 생일반차가 아닌 첫 옵션을 기본값으로 (HR이 흔히 사용하는 연차/병가가 기본)
    const firstNonBirthday = leaveTypeOptions.value.find((lt) => lt.name !== '생일반차')
    bulkForm.value.leave_type = firstNonBirthday?.id ?? leaveTypeOptions.value[0]?.id ?? null
  }
  await fetchCompanyOptions(currentYear)
})
</script>


<style scoped>
.summary-line { display: flex; flex-wrap: wrap; gap: 16px; padding: 10px 14px; background: #f5f7fa; border-radius: 6px; font-size: 14px; color: #5a6c7d; margin-bottom: 12px; }
.summary-line strong { color: #303133; font-weight: 700; }

</style>
